import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  isChunkError: boolean;
}

// Storage key for tracking reload attempts
const CHUNK_RELOAD_KEY = 'halr_chunk_reload_timestamp';

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  /**
   * Detect if an error is a chunk/module load failure
   */
  static isChunkLoadError(error: Error | undefined): boolean {
    if (!error) return false;
    const message = error.message || '';
    const name = error.name || '';
    return (
      message.includes('dynamically imported') ||
      message.includes('Failed to fetch') ||
      message.includes('Loading chunk') ||
      message.includes('ChunkLoadError') ||
      name === 'ChunkLoadError'
    );
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const isChunkError = ErrorBoundary.isChunkLoadError(error);
    return { hasError: true, error, isChunkError };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ error, errorInfo });
    console.error('[ErrorBoundary] Caught error:', error.message);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);

    // If it's a chunk error, attempt auto-refresh
    if (ErrorBoundary.isChunkLoadError(error)) {
      console.log('[ErrorBoundary] Chunk load error detected, attempting auto-refresh...');
      this.attemptAutoRefresh();
    }
  }

  /**
   * Auto-refresh the page on chunk errors, with protection against infinite loops
   */
  attemptAutoRefresh() {
    try {
      const lastReload = sessionStorage.getItem(CHUNK_RELOAD_KEY);
      const now = Date.now();

      // Only auto-refresh if we haven't done so in the last 30 seconds
      if (!lastReload || now - parseInt(lastReload) > 30000) {
        console.log('[ErrorBoundary] Auto-refreshing page...');
        sessionStorage.setItem(CHUNK_RELOAD_KEY, now.toString());
        window.location.reload();
      } else {
        console.log('[ErrorBoundary] Skipping auto-refresh (recently reloaded). Showing error UI.');
      }
    } catch (e) {
      // sessionStorage might not be available
      console.error('[ErrorBoundary] Could not access sessionStorage:', e);
    }
  }

  /**
   * Manual refresh - clears the reload tracking so it always works
   */
  handleRefresh = () => {
    try {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    } catch (e) {
      // Ignore sessionStorage errors
    }
    window.location.reload();
  };

  /**
   * Hard refresh - clears caches and forces fresh load
   */
  handleHardRefresh = () => {
    try {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      // Clear service worker caches if they exist
      if ('caches' in window) {
        caches.keys().then((names) => {
          names.forEach((name) => caches.delete(name));
        });
      }
    } catch (e) {
      // Ignore errors
    }
    // Force reload by adding cache-busting query param
    const url = window.location.href.split('?')[0];
    window.location.href = `${url}?_cache_bust=${Date.now()}`;
  };

  render() {
    if (this.state.hasError) {
      // Special user-friendly UI for chunk errors (looks like an update, not an error)
      if (this.state.isChunkError) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-neutral-95 p-4">
            <div className="bg-white rounded-card border border-amber-200 p-6 max-w-lg w-full shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-amber-700">
                  Update Available
                </h2>
              </div>
              <p className="text-neutral-600 mb-6">
                A new version of halr is available. Please refresh to get the latest updates and continue where you left off.
              </p>
              <div className="space-y-3">
                <button
                  onClick={this.handleRefresh}
                  className="w-full px-4 py-3 bg-neutral-90 text-white hover:bg-neutral-90/85 rounded-card transition-colors font-medium"
                >
                  Refresh Page
                </button>
                <button
                  onClick={this.handleHardRefresh}
                  className="w-full px-4 py-2 bg-neutral-100 text-neutral-700 hover:bg-neutral-200 rounded-card transition-colors text-sm"
                >
                  Hard Refresh (Clear Cache)
                </button>
              </div>
              <p className="text-xs text-neutral-400 mt-4 text-center">
                If this keeps happening, try clearing your browser cache or using incognito mode.
              </p>
            </div>
          </div>
        );
      }

      // Generic error UI for non-chunk errors
      return (
        <div className="min-h-screen flex items-center justify-center bg-neutral-95 p-4">
          <div className="bg-white rounded-card border border-red-200 p-6 max-w-lg w-full">
            <h2 className="text-lg font-semibold text-red-600 mb-4">Something went wrong</h2>
            <p className="text-neutral-90/80 mb-4">
              An unexpected error occurred. Please refresh the page or try again later.
            </p>
            <details className="mb-4">
              <summary className="cursor-pointer text-sm text-neutral-90/60 hover:text-neutral-10">
                Error Details
              </summary>
              <pre className="mt-2 text-xs bg-neutral-95 p-2 rounded overflow-auto max-h-32">
                {this.state.error && this.state.error.toString()}
              </pre>
              <pre className="mt-2 text-xs bg-neutral-95 p-2 rounded overflow-auto max-h-32">
                {this.state.errorInfo && this.state.errorInfo.componentStack}
              </pre>
            </details>
            <button
              onClick={this.handleRefresh}
              className="w-full px-4 py-2 bg-neutral-90 text-white hover:bg-neutral-90/85 rounded-card transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 